import React, { useRef, useState } from 'react';
import { FOCUS_RING } from './parts';

const FIELD = 'flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-white/20 bg-ink px-3.5 transition-[border-color,box-shadow] focus-within:border-sky focus-within:shadow-[0_0_0_3px_rgba(94,160,255,0.18)]';
const CONTROL = 'w-full min-w-0 border-0 bg-transparent p-0 text-cream outline-none placeholder:text-dim';
const BAD_ADDRESS = 'Enter a web address like yourbusiness.com.';

// A web address needs a dot and no spaces; the server does the real check.
const looksLikeAddress = (value) => value.includes('.') && !/\s/.test(value);

// "How does your website score?": the site URL only.
export default function CheckForm({ onSubmit, busy = false, error = '', className = '' }) {
  const [site, setSite] = useState('');
  const [localError, setLocalError] = useState('');
  const siteRef = useRef(null);
  const message = localError || error;

  const submit = (event) => {
    event.preventDefault();
    if (busy) return;
    const url = site.trim();
    if (!looksLikeAddress(url)) {
      setLocalError(BAD_ADDRESS);
      if (siteRef.current) siteRef.current.focus();
      return;
    }
    setLocalError('');
    if (onSubmit) onSubmit({ url });
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className={`rounded-[18px] border border-white/20 bg-gradient-to-b from-[rgba(23,34,56,0.92)] to-[rgba(18,26,42,0.92)] p-[18px] shadow-[0_24px_60px_-30px_rgba(29,78,216,0.55)] ${className}`}
    >
      <p className="m-0 mb-3 font-display text-[19px] font-semibold tracking-[-0.01em]">How does your website score?</p>
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <label htmlFor="site-check-url" className={`${FIELD} min-h-[52px]`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-none text-dim">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3Z" stroke="currentColor" strokeWidth="1.6" />
          </svg>
          <span className="sr-only">Your website</span>
          <input
            ref={siteRef}
            id="site-check-url"
            name="site"
            type="text"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            placeholder="yourbusiness.com"
            value={site}
            onChange={(e) => { setSite(e.target.value); if (localError) setLocalError(''); }}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? 'site-check-error' : undefined}
            className={`${CONTROL} text-base`}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className={`inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-cobalt px-[22px] text-[15.5px] font-medium text-white transition-colors hover:bg-[#2F62F0] disabled:cursor-not-allowed disabled:opacity-[.55] disabled:hover:bg-cobalt ${FOCUS_RING}`}
        >
          {busy ? 'Reading…' : 'Check my site'}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {message ? <p id="site-check-error" role="alert" className="mx-0.5 mb-0 mt-2 text-[13px] text-[#FF6B6B]">{message}</p> : null}
    </form>
  );
}
